/**
 * IndexedDB-backed refresh token store. Survives PWA eviction and iOS memory
 * pressure. Tokens are encrypted with AES-GCM using a non-extractable CryptoKey
 * also kept in IndexedDB — defence-in-depth against casual disk reads.
 */

const DB_NAME = 'understory-auth';
const STORE_NAME = 'tokens';
const DB_VERSION = 1;

const KEY_REF = 'encryption_key';
const TOKEN_REF = 'refresh_token';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function idbGet<T>(db: IDBDatabase, key: string): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db: IDBDatabase, key: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbDelete(db: IDBDatabase, key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getOrCreateKey(): Promise<CryptoKey> {
  const db = await openDB();
  const existing = await idbGet<CryptoKey>(db, KEY_REF);
  if (existing) return existing;

  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // non-extractable
    ['encrypt', 'decrypt'],
  );
  await idbPut(db, KEY_REF, key);
  return key;
}

interface StoredCiphertext {
  iv: ArrayBuffer;
  ciphertext: ArrayBuffer;
}

export async function storeRefreshToken(token: string): Promise<void> {
  const key = await getOrCreateKey();
  const ivView = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: ivView },
    key,
    new TextEncoder().encode(token),
  );

  // Store the underlying ArrayBuffer rather than the view so types stay
  // narrow (avoids Uint8Array<ArrayBufferLike> incompatibility with WebCrypto).
  const ivBuffer = ivView.buffer.slice(
    ivView.byteOffset,
    ivView.byteOffset + ivView.byteLength,
  ) as ArrayBuffer;

  const db = await openDB();
  await idbPut(db, TOKEN_REF, {
    iv: ivBuffer,
    ciphertext,
  } satisfies StoredCiphertext);
}

export async function getRefreshToken(): Promise<string | null> {
  try {
    const db = await openDB();
    const record = await idbGet<StoredCiphertext>(db, TOKEN_REF);
    if (!record) return null;

    const key = await getOrCreateKey();
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: record.iv },
      key,
      record.ciphertext,
    );
    return new TextDecoder().decode(plaintext);
  } catch {
    return null;
  }
}

export async function clearRefreshToken(): Promise<void> {
  try {
    const db = await openDB();
    await idbDelete(db, TOKEN_REF);
  } catch {
    // Best-effort cleanup.
  }
}
